import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ActionableNotification, Button, InlineNotification, Modal, Pagination, TextArea } from '@carbon/react';
import type { ColumnDef } from '@tanstack/react-table';
import { api } from '../../api/client';
import { useAuth } from '../../auth/AuthContext';
import { DataTable } from '../../components/DataTable';
import { InlineError, PageHeader } from '../../components/states';
import { Money, When } from '../../components/format';
import { FilterBar, useUrlFilters, type FilterField } from '../../components/Filters';
import { REGION, useLodeInfo } from '../../components/LodeChange';
import type { Command } from '../Commands';
import { TicketStatus } from './common';

type Ticket = {
  id: number; userId: number; nick: string; date?: string; city?: string; region?: string | null; channelName?: string; roundCode?: string;
  code: string; type?: string; numbers: string[]; perNumber?: number; stake: number; rate: number | null; status: string; hits?: number;
  payout: number; editedPayout: number | null; reason?: string | null; at: string; refundable: boolean; pendingCommand: string | null;
};
type Page = { items: Ticket[]; total: number; page: number; pageSize: number };

const GAME = [['lode', 'Lô đề 3 miền'], ['st', 'Lô đề siêu tốc'], ['keno', 'Keno']] as const;
const STATUS: Record<string, readonly (readonly [string, string])[]> = {
  lode: [['', 'Mọi trạng thái'], ['cho', 'Chờ kết quả'], ['trung', 'Trúng'], ['truot', 'Trượt'], ['hoan', 'Đã hoàn']],
  st: [['', 'Mọi trạng thái'], ['cho', 'Chờ kết quả'], ['trung', 'Trúng'], ['truot', 'Trượt']],
  keno: [['', 'Mọi trạng thái'], ['BET', 'Chờ kết quả'], ['WIN', 'Thắng'], ['LOSE', 'Thua']],
};
const KEYS = ['game', 'ticket', 'player', 'status', 'date', 'cityId', 'channel', 'round', 'code', 'number'];

export function LodeTicketsPage() {
  const { can } = useAuth();
  const nav = useNavigate();
  const qc = useQueryClient();
  const info = useLodeInfo().data;
  const f = useUrlFilters(KEYS, { defaults: { game: 'lode' } });
  const game = f.values.game || 'lode';
  const fields: FilterField[] = [
    { key: 'game', label: 'Trò', kind: 'select', options: GAME },
    { key: 'player', label: 'Người chơi (id, tên, nick)', kind: 'text' },
    { key: 'ticket', label: 'Mã vé', kind: 'number' },
    { key: 'status', label: 'Trạng thái', kind: 'select', options: STATUS[game]! },
    ...(game === 'lode' ? [
      { key: 'date', label: 'Ngày quay', kind: 'date' as const },
      { key: 'cityId', label: 'Đài', kind: 'select' as const, options: [['', 'Mọi đài'] as const, ...(info?.dai ?? []).map((d) => [String(d.id), `${d.name} (${REGION[d.region]})`] as const)] },
    ] : []),
    ...(game === 'st' ? [{ key: 'channel', label: 'Kênh', kind: 'select' as const, options: [['', 'Mọi kênh'] as const, ...(info?.sieuToc.kenh ?? []).map((k) => [String(k.id), k.name] as const)] }] : []),
    ...(game !== 'lode' ? [{ key: 'round', label: 'Kỳ (số nội bộ)', kind: 'number' as const }] : []),
    { key: 'code', label: game === 'keno' ? 'Cửa' : 'Mã loại cược', kind: 'text' },
    ...(game !== 'keno' ? [{ key: 'number', label: 'Có số', kind: 'number' as const }] : []),
  ];
  const q = useQuery({
    queryKey: ['lode-tickets', f.query, f.page, f.pageSize],
    queryFn: () => api.get<Page>('/api/admin/lode/tickets', { ...f.query, page: f.page, pageSize: f.pageSize }),
    placeholderData: keepPreviousData,
  });
  const [refund, setRefund] = useState<Ticket | null>(null);
  const [reason, setReason] = useState('');
  const [made, setMade] = useState<Command | null>(null);
  const m = useMutation({
    mutationFn: () => api.post<{ command: Command }>(`/api/admin/lode/tickets/${refund!.id}/refund`, { reason }),
    onSuccess: (r) => { setMade(r.command); setRefund(null); qc.invalidateQueries({ queryKey: ['lode-tickets'] }); },
  });

  const columns: ColumnDef<Ticket, any>[] = [
    { header: 'Mã vé', accessorKey: 'id', cell: (c) => <span className="num">{c.getValue()}</span> },
    { header: 'Lúc đặt', accessorKey: 'at', cell: (c) => <When v={c.getValue()} /> },
    { header: 'Người chơi', accessorKey: 'userId', cell: (c) => <Link className="cell-link" to={`/users/${c.getValue()}`}>{c.row.original.nick || c.getValue()}</Link> },
    game === 'lode'
      ? { header: 'Kỳ', id: 'draw', enableSorting: false, cell: (c) => <>{c.row.original.city} <span className="num muted">{c.row.original.date}</span></> }
      : { header: 'Kỳ', id: 'draw', enableSorting: false, cell: (c) => <>{c.row.original.channelName ?? ''} <span className="num">{c.row.original.roundCode}</span></> },
    { header: 'Cược', id: 'bet', enableSorting: false, cell: (c) => <>{c.row.original.type ?? c.row.original.code}: <span className="num">{c.row.original.numbers.join(' ')}</span></> },
    { header: 'Tiền cược', accessorKey: 'stake', cell: (c) => <Money v={c.getValue()} /> },
    { header: 'Tỷ lệ', accessorKey: 'rate', cell: (c) => <span className="num">{c.getValue() ?? '-'}</span> },
    { header: 'Trạng thái', accessorKey: 'status', cell: (c) => <TicketStatus s={c.getValue()} /> },
    { header: 'Đã trả', accessorKey: 'payout', cell: (c) => <Money v={c.row.original.editedPayout ?? c.getValue()} /> },
    ...(game === 'lode' && can('lode.manage') ? [{
      header: '', id: 'act', enableSorting: false,
      cell: (c: { row: { original: Ticket } }) => {
        const t = c.row.original;
        if (t.pendingCommand) return <Link className="cell-link" to={`/commands/${t.pendingCommand}`}>Lệnh hoàn đang chờ</Link>;
        return t.refundable ? <Button kind="ghost" size="sm" onClick={(e) => { e.stopPropagation(); m.reset(); setReason(''); setRefund(t); }}>Hoàn vé</Button> : null;
      },
    } as ColumnDef<Ticket, any>] : []),
  ];

  return (
    <div className="page">
      <PageHeader title="Vé lô đề, Keno, Siêu tốc"
        lead="Tìm vé theo người chơi, mã vé, kỳ, loại và số. Hoàn một vé lô đề 3 miền thực hiện ngay, không chờ người duyệt; vé đã chốt được hoàn cược và thu lại thưởng, ví chỉ đổi phần chênh." />
      <FilterBar id="lv" fields={fields} filters={f} exportPath="/api/admin/lode/tickets" exportQuery={{ game }} />
      {made ? <ActionableNotification kind={made.status === 'FAILED' ? 'error' : 'info'} inline lowContrast onClose={() => setMade(null)}
        title={made.status === 'FAILED' ? 'Hệ A từ chối lệnh hoàn vé' : made.status === 'SUCCEEDED' ? 'Đã hoàn vé' : 'Đã gửi lệnh hoàn vé, hệ A đang thực hiện'}
        subtitle="Mở lệnh để xem kết quả hệ A trả về." actionButtonLabel="Mở lệnh" onActionButtonClick={() => nav(`/commands/${made.id}`)} /> : null}
      <DataTable columns={columns} data={q.data?.items} loading={q.isLoading || q.isPlaceholderData} error={q.error} onRetry={() => q.refetch()}
        rowKey={(r) => String(r.id)} empty={{ title: 'Không có vé', body: f.active ? 'Không vé nào khớp bộ lọc.' : undefined }} />
      {q.data && q.data.total > 0 ? (
        <Pagination page={f.page} pageSize={f.pageSize} pageSizes={[25, 50, 100, 200]} totalItems={q.data.total}
          itemsPerPageText="Mỗi trang" pageRangeText={(_c, t) => `trên ${t} trang`}
          itemRangeText={(min, max, total) => `${min} đến ${max} trong ${total.toLocaleString('vi-VN')} vé`}
          onChange={({ page: p, pageSize: s }) => f.setPage(p, s)} />
      ) : null}
      <Modal open={!!refund} modalHeading={refund ? `Hoàn vé #${refund.id}` : ''} primaryButtonText="Hoàn ngay" secondaryButtonText="Đóng"
        primaryButtonDisabled={m.isPending || reason.trim().length < 5} onRequestClose={() => setRefund(null)} onRequestSubmit={() => m.mutate()}>
        {refund ? (
          <div className="stack">
            <dl className="facts">
              <dt>Người chơi</dt><dd>{refund.nick} <span className="num muted">{refund.userId}</span></dd>
              <dt>Kỳ</dt><dd>{refund.city} <span className="num">{refund.date}</span></dd>
              <dt>Cược</dt><dd>{refund.type}: <span className="num">{refund.numbers.join(' ')}</span></dd>
              <dt>Tiền cược trả lại</dt><dd><Money v={refund.stake} unit /></dd>
              {refund.payout > 0 ? <><dt>Thưởng thu lại</dt><dd><Money v={-refund.payout} signed unit /></dd></> : null}
            </dl>
            <TextArea id="refund-reason" labelText="Lý do" rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
              invalid={reason.length > 0 && reason.trim().length < 5} invalidText="Ít nhất 5 ký tự" />
            <InlineError error={m.error} />
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
